import React from "react";
import { StyledActionButton } from "./style.js";

type Variant =
  | "primary"
  | "secondary"
  | "subtle"
  | "ghost"
  | "purple"
  | "purpleOutline"
  | "danger";

type Size = "sm" | "md" | "lg";

interface SizeSpec {
  h: number;
  px: number;
  fs: number;
  gap: number;
}

interface VariantSpec {
  background: string;
  color: string;
  border: string;
  boxShadow?: string;
  fontWeight: number;
}

const BTN_SIZES: Record<Size, SizeSpec> = {
  sm: { h: 36, px: 14, fs: 13, gap: 6 },
  md: { h: 44, px: 18, fs: 14, gap: 8 },
  lg: { h: 52, px: 22, fs: 15, gap: 10 },
};

const BTN_VARIANTS: Record<Variant, VariantSpec> = {
  primary: {
    background: "linear-gradient(95deg,#01ECFF 0%,#6FFFFF 100%)",
    color: "#030039",
    border: "1px solid rgba(1,236,255,0.6)",
    boxShadow: "0 0 0 1px rgba(1,236,255,0.25),0 8px 24px rgba(1,236,255,0.25)",
    fontWeight: 600,
  },
  secondary: {
    background: "rgba(255,255,255,0.06)",
    color: "#FEFEFE",
    border: "1px solid rgba(255,255,255,0.16)",
    fontWeight: 500,
  },
  subtle: {
    background: "rgba(255,255,255,0.06)",
    color: "#D1C7FF",
    border: "1px solid rgba(255,255,255,0.14)",
    fontWeight: 500,
  },
  ghost: {
    background: "transparent",
    color: "#D1C7FF",
    border: "1px solid transparent",
    fontWeight: 500,
  },
  purple: {
    background: "linear-gradient(95deg,#962EFF 0%,#B97AFF 100%)",
    color: "#FEFEFE",
    border: "1px solid rgba(150,46,255,0.6)",
    boxShadow:
      "0 0 0 1px rgba(150,46,255,0.2),0 8px 24px rgba(150,46,255,0.25)",
    fontWeight: 600,
  },
  purpleOutline: {
    background: "transparent",
    color: "#FEFEFE",
    border: "1px solid rgba(150,46,255,0.55)",
    fontWeight: 500,
  },
  danger: {
    background: "#FF3B5B",
    color: "#FEFEFE",
    border: "none",
    boxShadow: "0 0 24px rgba(255,59,91,0.4)",
    fontWeight: 600,
  },
};

interface ActionButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "children"> {
  variant?: Variant;
  size?: Size;
  icon?: React.ReactNode;
  iconRight?: React.ReactNode;
  fullWidth?: boolean;
  children?: React.ReactNode;
}

const ActionButton = ({
  variant = "primary",
  size = "md",
  icon,
  iconRight,
  fullWidth = false,
  children,
  style,
  ...rest
}: ActionButtonProps) => {
  const s = BTN_SIZES[size];
  const v = BTN_VARIANTS[variant] ?? BTN_VARIANTS.primary;
  return (
    <StyledActionButton
      $h={s.h}
      $px={s.px}
      $fs={s.fs}
      $gap={s.gap}
      $background={v.background}
      $color={v.color}
      $border={v.border}
      $boxShadow={v.boxShadow}
      $fontWeight={v.fontWeight}
      $fullWidth={fullWidth}
      style={style}
      {...rest}
    >
      {icon}
      {children}
      {iconRight}
    </StyledActionButton>
  );
};

export default ActionButton;
